import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { SERVICES, COPY, RUNTIME } from "../lib/_data";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const inputCls =
  "bg-[#fffdf9] border-[#e5e0d8] focus-visible:ring-2 focus-visible:ring-[#c85a3c] focus-visible:ring-offset-0 focus-visible:border-[#c85a3c] rounded-lg h-12 text-[#2c2a29] placeholder:text-[#a8a29d]";

const DEFAULT_TIME_OPTIONS = [
  "Morning (8am – 12pm)",
  "Afternoon (12pm – 5pm)",
  "Evening (5pm – 7pm)",
  "Weekend",
];

export default function ConsultationForm({ variant = "card" }) {
  // Iter-77 — all visible form copy is editable via copy_overrides
  // (`forms.consultation.*`). Submission behavior is unchanged.
  const fc = (COPY.forms && COPY.forms.consultation) || {};
  const timeOptions = (fc.timeOptions
    ? fc.timeOptions.split(",").map((s) => s.trim()).filter(Boolean)
    : DEFAULT_TIME_OPTIONS);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    date: "",
    time: "",
    message: "",
  });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setSubmitting(true);
    try {
      if (RUNTIME.slug) {
        const serviceTitle =
          SERVICES.find((s) => s.slug === form.service)?.title || form.service;
        const details = [
          form.service && `Service: ${serviceTitle}`,
          form.date && `Preferred date: ${form.date}`,
          form.time && `Preferred time: ${form.time}`,
        ].filter(Boolean).join("\n");
        const message = ["[Consultation request]", details, form.message]
          .filter(Boolean).join("\n");
        const structured = {};
        if (serviceTitle) structured.service = serviceTitle;
        if (form.date) structured.date = form.date;
        if (form.time) structured.time = form.time;
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/site/${RUNTIME.slug}/contact`,
          { name: form.name, email: form.email, phone: form.phone, message, source: "consultation", details: structured }
        );
      }
      toast.success(fc.successTitle || "Consultation request received!", {
        description:
          fc.successBody || "A real person will reach out within the hour to confirm your time.",
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        service: "",
        date: "",
        time: "",
        message: "",
      });
    } catch (err) {
      toast.error(fc.errorTitle || "Something went wrong.", {
        description: fc.errorBody || "Please try again, or call us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const wrapCls =
    variant === "card"
      ? "bg-[#fffdf9] rounded-3xl border border-[#e5e0d8] p-6 md:p-10 shadow-[0_20px_60px_rgba(44,42,41,0.08)]"
      : "";

  return (
    <form
      data-testid="consultation-form"
      onSubmit={submit}
      noValidate
      className={wrapCls}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div>
          <Label htmlFor="c-name" className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.nameLabel || "Full Name *"}
          </Label>
          <Input
            id="c-name"
            data-testid="consult-name"
            className={`mt-2 ${inputCls}`}
            placeholder={fc.namePlaceholder || "Jane Doe"}
            value={form.name}
            onChange={set("name")}
            required
          />
        </div>
        <div>
          <Label htmlFor="c-phone" className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.phoneLabel || "Phone *"}
          </Label>
          <Input
            id="c-phone"
            type="tel"
            data-testid="consult-phone"
            className={`mt-2 ${inputCls}`}
            placeholder={fc.phonePlaceholder || "(555) 000-1234"}
            value={form.phone}
            onChange={set("phone")}
            required
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="c-email" className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.emailLabel || "Email *"}
          </Label>
          <Input
            id="c-email"
            type="email"
            data-testid="consult-email"
            className={`mt-2 ${inputCls}`}
            placeholder={fc.emailPlaceholder || "jane@email.com"}
            value={form.email}
            onChange={set("email")}
            required
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.serviceLabel || "Service Needed"}
          </Label>
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger
              data-testid="consult-service"
              className={`mt-2 ${inputCls}`}
            >
              <SelectValue placeholder={fc.servicePlaceholder || "Choose a service (or leave blank)"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-sure">Not sure yet — help me decide</SelectItem>
              {SERVICES.map((s) => (
                <SelectItem key={s.slug} value={s.slug}>
                  {s.title}
                </SelectItem>
              ))}
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="c-date" className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.dateLabel || "Preferred Date"}
          </Label>
          <Input
            id="c-date"
            type="date"
            data-testid="consult-date"
            className={`mt-2 ${inputCls}`}
            value={form.date}
            onChange={set("date")}
          />
        </div>
        <div>
          <Label htmlFor="c-time" className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.timeLabel || "Preferred Time"}
          </Label>
          <Select value={form.time} onValueChange={set("time")}>
            <SelectTrigger
              data-testid="consult-time"
              className={`mt-2 ${inputCls}`}
            >
              <SelectValue placeholder={fc.timePlaceholder || "Anytime that works"} />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="c-message" className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.messageLabel || "Tell us what's going on"}
          </Label>
          <Textarea
            id="c-message"
            data-testid="consult-message"
            className={`mt-2 min-h-[120px] ${inputCls}`}
            placeholder={fc.messagePlaceholder || "Describe the project or problem in your own words. No jargon required."}
            value={form.message}
            onChange={set("message")}
          />
        </div>
      </div>
      <button
        type="submit"
        data-testid="consult-submit"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#c85a3c] hover:bg-[#a64b32] disabled:opacity-70 text-[#fffdf9] font-bold px-8 py-4 rounded-full text-base transition-all shadow-[0_10px_28px_rgba(200,90,60,0.35)]"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {fc.sendingLabel || "Sending…"}
          </>
        ) : (
          <>
            {fc.submitLabel || "Submit Consultation Request"}
            <Send className="w-4 h-4" />
          </>
        )}
      </button>
      <p className="text-xs text-[#8a8580] text-center mt-4">
        {fc.footnote || "We reply within 60 minutes during business hours. No spam, ever."}
      </p>
    </form>
  );
}
